import { RefObject, useCallback, useRef, useState } from 'react';

export const useSpeechToText = (
  onResult: (text: string) => void,
  textareaRef?: RefObject<HTMLTextAreaElement | null>,
) => {
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTextRef = useRef('');
  const [isRecording, setIsRecording] = useState(false);

  const start = useCallback(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Speech Recognition не поддерживается браузером');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'ru-RU';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;

        if (result.isFinal) {
          finalTextRef.current += transcript + '\n';
        } else {
          interim += transcript;
        }
      }

      const nextText = finalTextRef.current + interim;
      onResult(nextText);

      // scroll + caret
      requestAnimationFrame(() => {
        const el = textareaRef?.current;
        if (!el) return;

        const len = el.value.length;
        el.selectionStart = len;
        el.selectionEnd = len;
        el.scrollTop = el.scrollHeight;
      });
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognition.onerror = () => {
      setIsRecording(false);
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
  }, [onResult]);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsRecording(false);
  }, []);

  const syncFromInput = useCallback((text: string) => {
    finalTextRef.current = text;
  }, []);

  const reset = useCallback(() => {
    finalTextRef.current = '';
  }, []);

  return {
    isRecording,
    start,
    stop,
    syncFromInput,
    reset,
  };
};
